import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form'
import { Modal } from '@/components/ui/modal'

export interface MaintenanceEditData {
  id: number
  titre: string
  description: string
  date: string
  temps_heures?: number
}

export interface EditMaintenanceModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: MaintenanceEditData) => void | Promise<void>
  isLoading?: boolean
  maintenance: MaintenanceEditData | null
}

export function EditMaintenanceModal({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
  maintenance,
}: EditMaintenanceModalProps) {
  const [formData, setFormData] = useState<MaintenanceEditData>({
    id: 0,
    titre: '',
    description: '',
    date: '',
    temps_heures: undefined,
  })

  // Update form data when maintenance prop changes
  useEffect(() => {
    if (maintenance) {
      // Ensure date is in YYYY-MM-DD format for the date input
      const formattedDate = maintenance.date.split('T')[0]
      setFormData({
        id: maintenance.id,
        titre: maintenance.titre,
        description: maintenance.description,
        date: formattedDate,
        temps_heures: maintenance.temps_heures,
      })
    }
  }, [maintenance])

  const handleInputChange = (field: keyof MaintenanceEditData, value: string | number | undefined) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async () => {
    if (!formData.titre.trim() || !formData.date.trim()) {
      return
    }

    await onSubmit(formData)
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      hideHeader={true}
      contentClassName="border-4 border-cyan-400 rounded-2xl shadow-2xl"
      footer={
        <div className="flex gap-3 pt-4 justify-end">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
            className="bg-white border-gray-200 hover:bg-gray-50"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isLoading}
            className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
          >
            Enregistrer les modifications
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="bg-white border-b border-gray-200 px-6 py-4 -mx-6 -mt-6 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Modifier la maintenance</h2>
            <p className="text-sm text-gray-500 mt-1">
              Modifiez les informations de cette intervention de maintenance
            </p>
          </div>
        </div>

        <FormField label="Titre" required>
          <Input
            type="text"
            value={formData.titre}
            onChange={(e) => handleInputChange('titre', e.target.value)}
            placeholder="Ex: Mise à jour modèle ML"
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
          />
        </FormField>

        <FormField label="Description">
          <Textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Décrivez l'intervention réalisée..."
            rows={4}
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent resize-none"
          />
        </FormField>

        <FormField label="Date" required>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => handleInputChange('date', e.target.value)}
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
          />
        </FormField>

        <FormField label="Temps passé (en heures)">
          <Input
            type="number"
            step="0.5"
            min="0"
            value={formData.temps_heures || ''}
            onChange={(e) => handleInputChange('temps_heures', e.target.value ? parseFloat(e.target.value) : undefined)}
            placeholder="Ex: 2.5"
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
          />
        </FormField>
      </div>
    </Modal>
  )
}

